import React from "react";
import { observer } from "mobx-react";
import { MusicalWorkViewStore } from "../../store";
import { MusicalWorkViewForm } from "./MusicalWorkViewForm";
import { ErrorDialog } from "../../../../common/controls";
import { DownloadIcon } from "../../../../icons";
import { VisibilityIcon } from "../../../../icons";
import { isOriginalCrudStatus, RowCrudStatus } from "../../../common/types";
import { useRootStore } from "../../../../store/contexts";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";
import { GroupContainer } from "../../../../common/components";
import { Box, Link, Typography } from "@mui/material";
import { getApiAsync } from "../../../../api";
import { signedEditionDisplayId } from "../../../../entities/signedEdition/store/SignedEditionCommonStore";
import { SignedEdition } from "../../../../api/graphql";
import { SortOrder } from "../../../../api/graphql";

type InverseSnapshotSectionProps = {
  sectionId: string;
  title: string;
  load: () => Promise<any[]>;
  renderLabel: (item: any) => string;
  onNavigate: (id: string) => void;
  watchId: string | undefined;
};

const InverseSnapshotSection: React.FC<InverseSnapshotSectionProps> = ({
  sectionId,
  title,
  load,
  renderLabel,
  onNavigate,
  watchId,
}) => {
  const [items, setItems] = React.useState<any[]>([]);
  const [loaded, setLoaded] = React.useState<boolean>(false);
  React.useEffect(() => {
    let cancelled = false;
    if (!watchId) {
      setItems([]);
      setLoaded(false);
      return;
    }
    (async () => {
      try {
        const list = await load();
        if (!cancelled) {
          setItems(list);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setItems([]);
          setLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watchId is the explicit trigger; load is an inline closure at the call site (over the same instance id and auth context as watchId), so re-fetching only when watchId changes is the documented intent
  }, [watchId]);
  if (!loaded || items.length === 0) return null;
  return (
    <GroupContainer id={sectionId} title={title}>
      <Box sx={{ p: 1 }}>
        {items.map((item: any) => (
          <Box key={item.id} sx={{ py: 0.5 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => onNavigate(item.id)}
              sx={{ textAlign: "left" }}
            >
              {renderLabel(item)}
            </Link>
          </Box>
        ))}
        {items.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </Box>
    </GroupContainer>
  );
};

export type MusicalWorkViewProps = {
  musicalWorkViewStore: MusicalWorkViewStore;
};

export const MusicalWorkView: React.FC<MusicalWorkViewProps> = observer(
  ({ musicalWorkViewStore }) => {
    const { authStore, activityStore, router, langStore } = useRootStore();

    const getViewAction = (
      onDownload: (props: {
        id: string;
        type: string;
        name: string;
        __status__: RowCrudStatus;
      }) => void,
      onViewPdf?: (props: any) => void
    ) => {
      const actions: any[] = [];
      if (onViewPdf) {
        actions.push({
          columnName: "__action_view_pdf",
          onClick: (props: any) =>
            isOriginalCrudStatus(props.__status__) ? onViewPdf(props) : {},
          icon: <VisibilityIcon />,
          label: langStore.viewCommand,
          isActionEnabled: (props: any) =>
            props.name?.toLowerCase().endsWith(".pdf"),
        });
      }
      actions.push({
        columnName: "__action_view",
        onClick: (props: any) =>
          isOriginalCrudStatus(props.__status__) ? onDownload(props) : {},
        icon: <DownloadIcon />,
        label: langStore.downloadCommand,
      });
      return actions;
    };

    return (
      <div>
        <MusicalWorkViewForm
          musicalWorkViewStore={musicalWorkViewStore}
          title={musicalWorkViewStore.titleValue}
          musicalGenreValue={musicalWorkViewStore.musicalGenreValue}
          alternativeTitles={musicalWorkViewStore.alternativeTitlesValue}
          authorsVisibleColumns={musicalWorkViewStore.authorsVisibleColumns}
          authorsSetColumnWiths={(x) =>
            musicalWorkViewStore.authorsSetColumnWiths(x)
          }
          authorsRows={musicalWorkViewStore.authorsRowsDyn}
          notes={musicalWorkViewStore.notesValue}
          performersValues={musicalWorkViewStore.performersValues}
          cmsId={musicalWorkViewStore.cmsIdValue}
          territoriesValues={musicalWorkViewStore.territoriesValues}
          iswc={musicalWorkViewStore.iswcValue}
          workNumber={musicalWorkViewStore.workNumberValue}
          scoreFiles={musicalWorkViewStore.scoreFiles}
          phonogramsVisibleColumns={
            musicalWorkViewStore.phonogramsVisibleColumns
          }
          phonogramsSetColumnWiths={(x) =>
            musicalWorkViewStore.phonogramsSetColumnWiths(x)
          }
          phonogramsRows={musicalWorkViewStore.phonogramsRowsDyn}
          originalLyrics={musicalWorkViewStore.originalLyricsValue}
          contractObservation={musicalWorkViewStore.contractObservationValue}
          createdAt_={musicalWorkViewStore.createdAt_Value}
          lastUpdatedAt_={musicalWorkViewStore.lastUpdatedAt_Value}
          onPrint={(handlePrint: () => void) =>
            musicalWorkViewStore.printMusicalWork(handlePrint)
          }
          onCancel={() => musicalWorkViewStore.cancelMusicalWork()}
          onEdit={
            musicalWorkViewStore.isEditEnabled
              ? () => musicalWorkViewStore.editMusicalWork()
              : undefined
          }
          scoreViewAction={getViewAction(
            (props) => musicalWorkViewStore.fileDownload(props, "score"),
            (props) => musicalWorkViewStore.viewFilePdf(props, "score")
          )}
          workWithAuthors={musicalWorkViewStore.workWithAuthors}
        />
        {musicalWorkViewStore.musicalWorkInstance?.id && (
          <>
            <InverseSnapshotSection
              key="__inverseSnapshot_signedEdition"
              sectionId="__inverseSnapshot_signedEdition"
              title={langStore.SignedEditionPlural}
              load={async () => {
                const id = musicalWorkViewStore.musicalWorkInstance?.id;
                if (!id) return [];
                const api = await getApiAsync(authStore, activityStore);
                const result = await api.signedEditionListWithOffset({
                  offset: 0,
                  take: 100,
                  where: { works: { some: { id: { equals: id } } } },
                  orderBy: [{ createdAt_: SortOrder.Desc }],
                });
                return (result.signedEditionListWithOffset ??
                  []) as SignedEdition[];
              }}
              renderLabel={(item: SignedEdition) =>
                signedEditionDisplayId(item, langStore)
              }
              onNavigate={(id: string) =>
                router.goTo(`/signedEdition/view/${id}`)
              }
              watchId={musicalWorkViewStore.musicalWorkInstance?.id}
            />
          </>
        )}
        <ErrorDialog
          open={musicalWorkViewStore.errorDialogOpened}
          title={musicalWorkViewStore.errorTitle}
          description={musicalWorkViewStore.errorDescription}
          onClose={() => musicalWorkViewStore.closeErrorDialog()}
        />
        <PDFViewerDialog
          open={musicalWorkViewStore.pdfViewerOpen}
          onClose={() => musicalWorkViewStore.closePdfViewer()}
          pdfUrl={musicalWorkViewStore.pdfViewerUrl}
          title={musicalWorkViewStore.pdfViewerTitle}
        />
      </div>
    );
  }
);
